//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import { css } from 'styled-components';

const FormControlDisabledOverrides = `
  color: ${theme.colors.gray4};
  background-color: ${theme.colors.gray0};
  border-color: ${theme.colors.gray2};
`;

const FocusProps = css`
  border-color: ${theme.colors.aliases.focus};
  background-color: ${theme.colors.white};
  outline: none;
  box-shadow:
    inset 0 0 0 ${theme.borderWidths[1]} ${theme.colors.aliases.focus},
    inset 0 0 0 ${theme.borderWidths[2]} ${theme.colors.cyan2};
`;

/**
 * Separate declarations of base styles so that they can be easily
 * extended in the primary mixins declaration.
 */
const FormControlBase = `
  border-radius: ${theme.borderRadii[1]};
  box-sizing: border-box;
  height: ${theme.spacings[5]};
  padding: ${theme.spacings[2]};
  color: ${theme.colors.aliases.bodyPrimary};
  border: ${theme.borderWidths[1]} solid ${theme.colors.gray2};

  &::placeholder {
    color: ${theme.colors.gray4};
    opacity: 1;
  }

  &[disabled] {
    ${FormControlDisabledOverrides}
  }

  &:not([disabled]) {
    &:hover {
      box-shadow: ${theme.boxShadows[1]};

      &:read-only {
        box-shadow: none;
      }
    }

    &:focus,
    &.cu-focus {
      ${FocusProps}

      &:read-only {
        box-shadow: none;
      }
    }
  }
`;

/*
 * This set of mixins allows you to add sets of Unison styles to React
 * styled-components without potential css specificity conflicts. These
 * should take the place of attaching a Unison style class to a component
 * via the .attrs() helper.
 *
 * Usage:
 *
 * const FlexSheet = styled.div`
 *   ${ Mixins.Sheet }
 *   display: flex;
 * `;
 *
 * const SuccessTitle = styled.div`
 *   ${ Mixins.Text.Title }
 *   color: ${ theme.colors.aliases.success };
 * `;
 *
 */

export const Mixins = {
  Text: {
    Display: `
      font-size: ${theme.fontSizes[0]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.titleLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    Title: `
      font-size: ${theme.fontSizes[1]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.titleLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    Subtitle: `
      font-size: ${theme.fontSizes[2]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.titleLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    Section: `
      font-size: ${theme.fontSizes[3]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.titleLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    SectionBold: `
      font-size: ${theme.fontSizes[3]};
      font-weight: ${theme.fontWeights.bold};
      line-height: ${theme.lineHeights.titleLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    Subsection: `
      font-size: ${theme.fontSizes[4]};
      font-weight: ${theme.fontWeights.semiBold};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    Metatag: `
      font-size: ${theme.fontSizes[6]};
      font-weight: ${theme.fontWeights.bold};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.bodySecondary};
      text-transform: uppercase;
      letter-spacing: .1em;
    `,
    Badge: `
      font-size: ${theme.fontSizes[7]};
      font-weight: ${theme.fontWeights.semiBold};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.bodySecondary};
      text-transform: uppercase;
      letter-spacing: .1em;
    `,
    Body: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    BodyBold: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.bold};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    BodySecondary: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.bodySecondary};
    `,
    BodyDisabled: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.disabled};
    `,
    BodyError: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.error};
    `,
    UiCopy: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    UiCopyBold: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.bold};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    UiCopySecondary: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.bodySecondary};
    `,
    UiCopyDisabled: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.disabled};
    `,
    UiCopyError: `
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.error};
    `,
    Caption: `
      font-size: ${theme.fontSizes[6]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    CaptionSecondary: `
      font-size: ${theme.fontSizes[6]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.bodySecondary};
    `,
    CaptionError: `
      font-size: ${theme.fontSizes[6]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.error};
    `,
    Label: `
      font-size: ${theme.fontSizes[6]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.uiLeading};
      color: ${theme.colors.aliases.bodyPrimary};
    `,
    LinkPrimary: `
      &&, &&:hover {
        font-size: ${theme.fontSizes[5]};
        font-weight: ${theme.fontWeights.normal};
        line-height: ${theme.lineHeights.bodyLeading};
        color: ${theme.colors.aliases.primary};

        &[disabled], &.cu-disabled {
          color: ${theme.colors.aliases.disabled};
          text-decoration: none;
          cursor: not-allowed;
        }
      }
    `,
    LinkCaptionPrimary: `
      &, &:hover {
        font-size: ${theme.fontSizes[6]};
        font-weight: ${theme.fontWeights.normal};
        line-height: ${theme.lineHeights.bodyLeading};
        color: ${theme.colors.aliases.primary};

        &[disabled], &.cu-disabled {
          color: ${theme.colors.aliases.disabled};
          text-decoration: none;
          cursor: not-allowed;
        }
      }
    `,
    LinkBodyPrimary: `
      &, &:hover {
        font-size: ${theme.fontSizes[5]};
        font-weight: ${theme.fontWeights.normal};
        line-height: ${theme.lineHeights.bodyLeading};
        color: ${theme.colors.aliases.bodyPrimary};

        &[disabled], &.cu-disabled {
          color: ${theme.colors.aliases.disabled};
          text-decoration: none;
          cursor: not-allowed;
        }
      }
    `,
    LinkSecondary: `
    &, &:hover {
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
      color: ${theme.colors.aliases.bodySecondary};

      &[disabled], &.cu-disabled {
        color: ${theme.colors.aliases.disabled};
        text-decoration: none;
        cursor: not-allowed;
      }
    }
  `,
    LinkBodySecondary: `
      && {
        color: ${theme.colors.aliases.primary};
      }
      
      border-radius: 500px;
      box-shadow: inset 0 0 0 1.5px ${theme.colors.aliases.primary};
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.bold};
      line-height: ${theme.lineHeights.bodyLeading};
      text-decoration: none;
      
      &&:hover {
        background-color: ${theme.colors.aliases.primaryShade1};
        box-shadow: inset 0 0 0 1.5px ${theme.colors.aliases.primaryShade1};
        color: ${theme.colors.white};
        text-decoration: none;
      }
    `,
    TableHeaderCell: `
      color: ${theme.colors.gray8};
      font-size: ${theme.fontSizes[6]};
      font-weight: ${theme.fontWeights.bold};
      letter-spacing: .1em;
      line-height: ${theme.lineHeights.uiLeading};
      text-transform: uppercase;
    `,
    TableDataPrimary: `
      color: ${theme.colors.gray9};
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
    `,
    TableDataSecondary: `
      color: ${theme.colors.gray7};
      font-size: ${theme.fontSizes[5]};
      font-weight: ${theme.fontWeights.normal};
      line-height: ${theme.lineHeights.bodyLeading};
    `,
  },
  Sheet: {
    Low: `
      display: flex;
      background-color: ${theme.colors.white};
      box-shadow: ${theme.boxShadows[1]};
    `,
    Medium: `
      display: flex;
      background-color: ${theme.colors.white};
      box-shadow: ${theme.boxShadows[2]};
    `,
    High: `
      display: flex;
      background-color: ${theme.colors.white};
      box-shadow: ${theme.boxShadows[3]};
    `,
  },
  FormControl: {
    Primary: FormControlBase,
    Error:
      FormControlBase +
      `
      border-color: ${theme.colors.aliases.error};
      background-color: ${theme.colors.red0};
      outline: 0;
    `,
    Disabled: FormControlBase + FormControlDisabledOverrides,
  },
  Border: {
    MediaError: `
      border: ${theme.borderWidths[2]} solid ${theme.colors.aliases.error}
    `,
  },
  /**
   * VisuallyHidden mixin provides styles for components to be completely invisible to sighted readers,
   * but properly read by screen readers on all browsers.
   *
   * For more information on this approach, see this article: https://snook.ca/archives/html_and_css/hiding-content-for-accessibility
   */
  VisuallyHidden: `
    position: absolute !important;
    height: 1px;
    width: 1px;
    padding: 0 !important;
    border: 0 !important;
    overflow: hidden;
    white-space: nowrap !important;
    clip: rect(1px, 1px, 1px, 1px);
  `,
  Focus: FocusProps,
} as const;

export type TextMixin = (typeof Mixins)['Text'][keyof (typeof Mixins)['Text']];
